<!-- src/components/ModelViewer.vue -->
<template>
  <div class="renderer-wrapper">
    <div
      ref="rendererContainer"
      class="renderer-container"
      :class="{ 'is-ready': viewerReady }"
    ></div>

    <div v-if="!viewerReady" class="panel-loader" aria-live="polite">
      <div class="spinner"></div>
      <div class="loader-title">{{ $t('modelViewer.title') }}</div>
      <div class="loader-hint">{{ loadingHint }}</div>
      <div class="loader-bar">
        <span class="loader-fill" :style="{ width: `${loadProgress}%` }"></span>
      </div>
      <div class="loader-progress">{{ loadProgress }}%</div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import { markRaw } from 'vue';

export default {
  name: 'ModelViewer',
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      model: null,
      modelBaseY: 19,
      orbitalRing: null,
      animationFrameId: null,
      loading: true,
      loadError: false,
      viewerReady: false,
      loadProgress: 0,
      loadingHint: this.$t('modelViewer.initializing'),
      resizeObserver: null,
      resizeRafId: 0,
      lastWidth: 0,
      lastHeight: 0,
      lastDpr: 0,
      modelLoadAttempts: 0,
      retryTimer: null,
      finalizeRafId: 0,
      cacheRecoveryDone: false,
      activeLoadToken: 0,
      rebuildInProgress: false,
      onContextLostHandler: null,
      onContextRestoredHandler: null
    }
  },
  mounted() {
    this.initThree();
    this.loadModel();
    this.animate();
    window.addEventListener('resize', this.scheduleResize);
    window.addEventListener('pageshow', this.handleAppVisible);
    document.addEventListener('visibilitychange', this.handleAppVisible);
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleResize();
      });
      this.resizeObserver.observe(this.$refs.rendererContainer);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.scheduleResize);
    window.removeEventListener('pageshow', this.handleAppVisible);
    document.removeEventListener('visibilitychange', this.handleAppVisible);
    cancelAnimationFrame(this.animationFrameId);
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = 0;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.finalizeRafId) {
      cancelAnimationFrame(this.finalizeRafId);
      this.finalizeRafId = 0;
    }
    this.activeLoadToken += 1;
    this.disposeModel();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.disposeRenderer();
  },
  methods: {
    initThree() {
      if (this.renderer && this.scene && this.camera) return;
      this.scene = markRaw(new THREE.Scene());
      this.scene.background = null;
      const width = this.$refs.rendererContainer.clientWidth;
      const height = this.$refs.rendererContainer.clientHeight;
      const coarsePointer = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
      this.camera = markRaw(new THREE.PerspectiveCamera(48, width / height, 0.1, 1000));
      this.camera.position.set(0, 10, 220);
  
      this.renderer = markRaw(new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
        premultipliedAlpha: true
      }));
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, coarsePointer ? 1.05 : 1.3));
      this.renderer.setSize(width || 1, height || 1);
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.15;
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.domElement.style.position = 'absolute';
      this.renderer.domElement.style.inset = '0';
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';
      this.renderer.domElement.style.zIndex = '2';
      this.renderer.domElement.style.display = 'block';
      this.renderer.domElement.style.transform = 'translateZ(0)';
      this.renderer.domElement.style.webkitTransform = 'translateZ(0)';
      this.renderer.domElement.style.webkitBackfaceVisibility = 'hidden';
      this.renderer.domElement.style.backfaceVisibility = 'hidden';
      this.renderer.domElement.style.willChange = 'transform, opacity';
      this.$refs.rendererContainer.appendChild(this.renderer.domElement);
      this.attachContextRecovery();
      this.applyResize(true);
  
      const ambientLight = markRaw(new THREE.AmbientLight(0x88b7ff, 0.8));
      this.scene.add(ambientLight);

      const hemi = markRaw(new THREE.HemisphereLight(0x9fd6ff, 0x0b1225, 0.95));
      this.scene.add(hemi);

      const keyLight = markRaw(new THREE.DirectionalLight(0xffd3a3, 1.25));
      keyLight.position.set(38, 58, 44);
      this.scene.add(keyLight);

      const fillLight = markRaw(new THREE.DirectionalLight(0x90d4ff, 0.95));
      fillLight.position.set(-42, 22, 28);
      this.scene.add(fillLight);

      const rimLight = markRaw(new THREE.PointLight(0x7ed1ff, 1.3, 360));
      rimLight.position.set(0, 14, -48);
      this.scene.add(rimLight);

      const ringGeometry = new THREE.TorusGeometry(70, 1.3, 18, 128);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: 0x92d8ff,
        transparent: true,
        opacity: 0.22
      });
      this.orbitalRing = markRaw(new THREE.Mesh(ringGeometry, ringMaterial));
      this.orbitalRing.rotation.x = Math.PI / 2;
      this.orbitalRing.position.y = -22;
      this.scene.add(this.orbitalRing);
    },
    attachContextRecovery() {
      if (!this.renderer || !this.renderer.domElement) return;
      const canvas = this.renderer.domElement;
      this.onContextLostHandler = (event) => {
        event.preventDefault();
        this.rebuildRenderer(this.$t('modelViewer.contextLost'));
      };
      this.onContextRestoredHandler = () => {
        this.scheduleResize();
      };
      canvas.addEventListener('webglcontextlost', this.onContextLostHandler, false);
      canvas.addEventListener('webglcontextrestored', this.onContextRestoredHandler, false);
    },
    detachContextRecovery() {
      if (!this.renderer || !this.renderer.domElement) return;
      const canvas = this.renderer.domElement;
      if (this.onContextLostHandler) {
        canvas.removeEventListener('webglcontextlost', this.onContextLostHandler, false);
      }
      if (this.onContextRestoredHandler) {
        canvas.removeEventListener('webglcontextrestored', this.onContextRestoredHandler, false);
      }
      this.onContextLostHandler = null;
      this.onContextRestoredHandler = null;
    },
    disposeRenderer() {
      if (!this.renderer) return;
      this.detachContextRecovery();
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
      this.renderer = null;
      this.camera = null;
      this.scene = null;
      this.orbitalRing = null;
      this.lastWidth = 0;
      this.lastHeight = 0;
      this.lastDpr = 0;
    },
    getModelUrl() {
      const basePath = `${process.env.BASE_URL}models/logo.glb`;
      // Add a changing query string after the first attempt to bypass potentially stale SW/cache entries.
      if (this.modelLoadAttempts <= 1) return basePath;
      const separator = basePath.includes('?') ? '&' : '?';
      return `${basePath}${separator}retry=${this.modelLoadAttempts}-${Date.now()}`;
    },
    async purgeModelCaches() {
      if (typeof window === 'undefined' || !('caches' in window)) return;

      try {
        const modelPath = `${process.env.BASE_URL}models/logo.glb`;
        const absoluteModelUrl = new URL(modelPath, window.location.origin).toString();
        const cacheNames = await caches.keys();

        await Promise.all(cacheNames.map(async (cacheName) => {
          if (!cacheName.includes('workbox') && !cacheName.includes('models-cache')) return;
          const cache = await caches.open(cacheName);
          await Promise.all([
            cache.delete(modelPath),
            cache.delete(absoluteModelUrl)
          ]);
        }));
      } catch (error) {
        // best effort cache recovery
        console.warn('Model cache purge skipped:', error);
      }
    },
    loadModel() {
      if (this.model) return;
      if (!this.scene || !this.camera || !this.renderer) {
        this.initThree();
      }
      const loader = new GLTFLoader();
      const loadToken = ++this.activeLoadToken;
      this.modelLoadAttempts += 1;
      this.loadError = false;
      this.viewerReady = false;
      this.loading = true;
      this.loadProgress = Math.max(this.loadProgress, 4);
      this.loadingHint = this.modelLoadAttempts === 1
        ? this.$t('modelViewer.cacheLoad')
        : this.$t('modelViewer.retrying', { count: this.modelLoadAttempts });

      loader.load(
        this.getModelUrl(),
        (gltf) => {
          if (loadToken !== this.activeLoadToken || !this.scene) return;
          this.loadError = false;
          this.cacheRecoveryDone = false;

          const root = gltf?.scene || gltf?.scenes?.[0];
          if (!root) {
            throw new Error(this.$t('modelViewer.invalidScene'));
          }

          const logoMaterial = new THREE.MeshPhysicalMaterial({
            color: 0xf5f8ff,
            metalness: 0.16,
            roughness: 0.3,
            clearcoat: 0.85,
            clearcoatRoughness: 0.24,
            reflectivity: 0.65
          });

          root.traverse((child) => {
            if (!child.isMesh) return;
            if (child.geometry && !child.geometry.attributes?.normal) {
              child.geometry.computeVertexNormals();
            }
            child.castShadow = false;
            child.receiveShadow = false;
            child.material = logoMaterial;

            const edges = new THREE.EdgesGeometry(child.geometry, 20);
            const edgeLines = markRaw(new THREE.LineSegments(edges, new THREE.LineBasicMaterial({
              color: 0x8fd4ff,
              transparent: true,
              opacity: 0.26
            })));
            child.add(edgeLines);
          });

          const box = new THREE.Box3().setFromObject(root);
          const center = box.getCenter(new THREE.Vector3());
          root.position.sub(center);

          this.model = markRaw(root);
          this.model.position.y = this.modelBaseY
          // STL source was "lying down" and needed a -90deg X rotation.
          // The GLB export is already front-facing, so keep it unrotated on X.
          this.model.rotation.x = 0;
          this.model.rotation.y = 0;
          this.model.rotation.z = 0;
          // Match the previous on-screen size of the STL logo.
          this.model.scale.set(122.5, 122.5, 202.5);

          this.scene.add(this.model);

          gsap.to(this.model.rotation, {
            y: Math.PI * 2,
            duration: 12,
            repeat: -1,
            ease: "linear"
          });

          this.loadProgress = 96;
          this.loadingHint = this.$t('modelViewer.finalizing');
          this.loading = false;
          this.finalizeViewerReady();
        },
        (xhr) => {
          if (loadToken !== this.activeLoadToken) return;
          if (!xhr) return;
          if (xhr.total && xhr.total > 0) {
            const progress = Math.min(99, Math.round((xhr.loaded / xhr.total) * 100));
            this.loadProgress = Math.max(this.loadProgress, progress);
            this.loadingHint = this.$t('modelViewer.downloading');
          } else if (xhr.loaded > 0) {
            this.loadProgress = Math.min(95, this.loadProgress + 2);
            this.loadingHint = this.$t('modelViewer.receiving');
          }
        },
        (error) => {
          if (loadToken !== this.activeLoadToken) return;
          if (error) {
            console.warn('Logo GLB load failed, retrying...', error);
          }
          this.disposeModel();
          this.loadError = false;
          this.viewerReady = false;
          this.loading = true;
          this.loadProgress = Math.min(this.loadProgress, 32);
          if (!this.cacheRecoveryDone) {
            this.cacheRecoveryDone = true;
            this.loadingHint = this.$t('modelViewer.cacheRecovery');
            this.purgeModelCaches();
          }
          const delay = Math.min(8000, 900 * this.modelLoadAttempts);
          this.loadingHint = this.$t('modelViewer.slowRetry', { seconds: Math.ceil(delay / 1000) });
          if (this.retryTimer) clearTimeout(this.retryTimer);
          this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.loadModel();
          }, delay);
        }
      );
    },
    finalizeViewerReady() {
      if (this.finalizeRafId) {
        cancelAnimationFrame(this.finalizeRafId);
        this.finalizeRafId = 0;
      }
      let checks = 0;
      const maxChecks = 24;

      const tick = () => {
        checks += 1;
        this.scheduleResize();

        if (this.renderer && this.camera) {
          if (this.renderer.info && this.renderer.info.reset) this.renderer.info.reset();
          this.renderer.render(this.scene, this.camera);
        }

        const canvas = this.renderer && this.renderer.domElement;
        const gl = this.renderer && this.renderer.getContext ? this.renderer.getContext() : null;
        const contextHealthy = !gl || (typeof gl.isContextLost !== 'function') || !gl.isContextLost();
        const renderInfo = this.renderer && this.renderer.info ? this.renderer.info.render : null;
        const hasDrawnGeometry = Boolean(renderInfo && renderInfo.calls > 0 && renderInfo.triangles > 0);
        const ready = Boolean(
          this.model &&
          canvas &&
          canvas.width > 2 &&
          canvas.height > 2 &&
          contextHealthy &&
          hasDrawnGeometry
        );

        if (ready) {
          this.viewerReady = true;
          this.loadProgress = 100;
          this.loadingHint = this.$t('modelViewer.ready');
          this.finalizeRafId = 0;
          return;
        }

        if (checks < maxChecks) {
          this.finalizeRafId = requestAnimationFrame(tick);
          return;
        }

        this.finalizeRafId = 0;
        this.rebuildRenderer(this.$t('modelViewer.unstableRender'));
      };

      this.finalizeRafId = requestAnimationFrame(tick);
    },
    rebuildRenderer(message = '') {
      if (this.rebuildInProgress) return;
      this.rebuildInProgress = true;

      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      if (this.finalizeRafId) {
        cancelAnimationFrame(this.finalizeRafId);
        this.finalizeRafId = 0;
      }

      this.activeLoadToken += 1; // invalidate stale async loader callbacks
      this.viewerReady = false;
      this.loading = true;
      this.loadError = false;
      this.loadingHint = message || this.$t('modelViewer.restoring');
      this.loadProgress = Math.min(this.loadProgress, 28);
      this.disposeModel();
      this.disposeRenderer();

      const delay = Math.min(2600, 500 + this.modelLoadAttempts * 200);
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.initThree();
        this.rebuildInProgress = false;
        this.loadModel();
      }, delay);
    },
    disposeModel() {
      if (!this.model) return;
      gsap.killTweensOf(this.model.rotation);
      if (this.scene) this.scene.remove(this.model);

      const material = this.model.material;
      const geometry = this.model.geometry;

      if (this.model.children && this.model.children.length) {
        this.model.children.forEach((child) => {
          if (child.geometry && child.geometry.dispose) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((m) => m && m.dispose && m.dispose());
            } else if (child.material.dispose) {
              child.material.dispose();
            }
          }
        });
      }

      if (geometry && geometry.dispose) geometry.dispose();
      if (material) {
        if (Array.isArray(material)) {
          material.forEach((m) => m && m.dispose && m.dispose());
        } else if (material.dispose) {
          material.dispose();
        }
      }
      this.model = null;
    },
    handleAppVisible() {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      this.scheduleResize();
      if (!this.model && !this.loading) {
        this.modelLoadAttempts = 0;
        this.cacheRecoveryDone = false;
        this.loadProgress = 0;
        this.loadingHint = this.$t('modelViewer.restoring3d');
        this.loadError = false;
        this.viewerReady = false;
        this.loadModel();
      }
    },
    animate() {
      this.animationFrameId = requestAnimationFrame(this.animate);
      if (!this.renderer || !this.camera) return;
      const t = performance.now() * 0.001;
      if (this.model) {
        this.model.position.y = this.modelBaseY + Math.sin(t * 1.35) * 2.4;
      }
      if (this.orbitalRing) {
        this.orbitalRing.rotation.z = t * 0.18;
        this.orbitalRing.material.opacity = 0.18 + (Math.sin(t * 2.2) + 1) * 0.06;
      }
      this.renderer.render(this.scene, this.camera);
    },
    scheduleResize() {
      if (this.resizeRafId) return;
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = 0;
        this.applyResize();
      });
    },
    applyResize(force = false) {
      if (!this.renderer || !this.camera || !this.$refs.rendererContainer) return;
      const container = this.$refs.rendererContainer;
      let width = container.clientWidth;
      let height = container.clientHeight;
      if (width < 2 && container.parentElement) {
        width = container.parentElement.clientWidth;
      }
      if (height < 2 && container.parentElement) {
        height = container.parentElement.clientHeight;
      }
      width = Math.max(width, 280);
      height = Math.max(height, 320);
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

      if (!force && width === this.lastWidth && height === this.lastHeight && dpr === this.lastDpr) {
        return;
      }

      this.lastWidth = width;
      this.lastHeight = height;
      this.lastDpr = dpr;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setPixelRatio(dpr);
      // Avoid writing CSS width/height every cycle to prevent ResizeObserver feedback loops.
      this.renderer.setSize(width, height, false);
    }
  }
}
</script>

<style scoped>
.renderer-wrapper {
  position: relative;
  z-index: 2;
  isolation: isolate;
  width: 100%;
}

/* Container del renderer */
.renderer-container {
  width: min(100%, 620px);
  min-width: 0;
  height: clamp(320px, 45vw, 520px);
  margin: auto;
  position: relative;
  border-radius: 0;
  border: 0;
  background: transparent;
  overflow: visible;
  isolation: isolate;
  transform: translateZ(0);
  will-change: transform;
  box-shadow: none;
}

.renderer-container canvas {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  z-index: 2;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.renderer-container.is-ready canvas {
  opacity: 1;
}

.renderer-container::before {
  display: none;
}

.panel-loader {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.25rem;
  text-align: center;
  background: transparent;
  border-radius: 0;
  backdrop-filter: none;
  pointer-events: none;
}

.fallback-message {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  background: rgba(8, 0, 20, 0.8);
  border-radius: 26px;
  font-size: 1.05rem;
  color: #f5f6ff;
  backdrop-filter: blur(6px);
}

.fallback-message p {
  margin: 0 0 0.8rem;
}

.retry-btn {
  border: 1px solid rgba(255, 199, 140, 0.45);
  background: linear-gradient(120deg, rgba(255, 194, 128, 0.2), rgba(126, 202, 255, 0.2));
  color: #eef7ff;
  border-radius: 999px;
  padding: 0.52rem 1.05rem;
  font-weight: 600;
  cursor: pointer;
}

.loader-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: #f4f8ff;
}

.loader-hint {
  color: rgba(205, 217, 236, 0.86);
  font-size: 0.86rem;
}

.loader-bar {
  margin-top: 0.2rem;
  width: min(260px, 85%);
  height: 9px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.loader-fill {
  display: block;
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffc98d, #8bd5ff);
  transition: width 0.24s ease;
}

.loader-progress {
  font-size: 0.86rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(186, 227, 255, 0.95);
}

/* Spinner */
.spinner {
  border: 5px solid rgba(255, 255, 255, 0.23);
  border-top: 5px solid #ffcb95;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  animation: spin 1s linear infinite;
  margin-bottom: 0.45rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
